import { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import BuildsView from '../components/builds/BuildsView';
import type { BuildSummary } from '../components/builds/BuildsView';
import { Button, Dropdown, Input, SearchableSelect, SidePanel, StatCard, Switch } from '../components/ui';
import { useUrlParam } from '../hooks/useUrlParam';
import { supabase } from '../lib/supabase';
import { formatForDateInput, getCurrentDate } from '../utils/dateUtils';
import {
  DAY_LABELS, addPage, deleteBuild, deletePage, fetchBuilds, fetchPageCounts, fetchPages,
  releaseNext, reorderPages, saveBuild, updatePage,
} from '../lib/activeBuilds';
import type { ActiveBuild, BuildFreq, BuildPage, BuildStatus, PageKind } from '../lib/activeBuilds';

interface BuildForm {
  client_id: string | null;
  name: string;
  notes: string;
  status: BuildStatus;
  assigned_to: string | null;
  freq: BuildFreq;
  interval_n: string;
  byweekday: number[];
  bymonthday: string;
  start_date: string;
  hold_while_open: boolean;
  auto_release: boolean;
}

const emptyForm = (): BuildForm => ({
  client_id: null,
  name: 'Website build',
  notes: '',
  status: 'active',
  assigned_to: null,
  freq: 'weekly',
  interval_n: '1',
  byweekday: [1],
  bymonthday: '1',
  start_date: formatForDateInput(getCurrentDate()),
  hold_while_open: false,
  auto_release: false,
});

export default function ActiveBuilds() {
  const [builds, setBuilds] = useState<ActiveBuild[]>([]);
  const [counts, setCounts] = useState<Record<string, { done: number; queued: number; backlog: number }>>({});
  const [pages, setPages] = useState<BuildPage[]>([]);
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [users, setUsers] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [filter, setFilter] = useState<BuildStatus>('active');
  // The build on screen is ?build=<id>, so refresh, back and shared links land on the same client.
  const [buildParam, setBuildParam] = useUrlParam('build');

  const [panelOpen, setPanelOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BuildForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeleteBuild, setConfirmDeleteBuild] = useState(false);

  const loadBuilds = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([fetchBuilds(), fetchPageCounts()]);
      setBuilds(b);
      setCounts(c);
    } catch (e) {
      console.error('Error loading builds:', e);
      setError('Could not load builds.');
    }
  }, []);

  useEffect(() => {
    (async () => {
      const [clientRes, userRes] = await Promise.all([
        supabase.from('clients').select('id, name').order('name'),
        supabase.from('users').select('id, name, email, role').neq('role', 'client').order('name'),
      ]);
      if (!clientRes.error) setClients((clientRes.data as Array<{ id: string; name: string }>) ?? []);
      if (!userRes.error) {
        setUsers(((userRes.data ?? []) as Array<{ id: string; name: string | null; email: string }>).map((u) => ({ id: u.id, name: u.name ?? u.email })));
      }
      await loadBuilds();
      setLoading(false);
    })();
  }, [loadBuilds]);

  const summaries: BuildSummary[] = useMemo(
    () => builds.map((b) => ({ build: b, ...(counts[b.id] ?? { done: 0, queued: 0, backlog: 0 }) })),
    [builds, counts],
  );

  const current = useMemo(() => {
    const byParam = builds.find((b) => b.id === buildParam);
    if (byParam) return byParam;
    return builds
      .filter((b) => b.status === filter)
      .sort((a, b) => (a.client?.name ?? '').localeCompare(b.client?.name ?? ''))[0] ?? null;
  }, [builds, buildParam, filter]);

  // A link to a paused/finished build shows that build's filter
  useEffect(() => {
    if (current && current.id === buildParam && current.status !== filter) setFilter(current.status);
  }, [current, buildParam, filter]);

  const loadPages = useCallback(async (buildId: string) => {
    try {
      const rows = await fetchPages(buildId);
      setPages(rows);
      const c = { done: 0, queued: 0, backlog: 0 };
      rows.forEach((r) => { c[r.status] += 1; });
      setCounts((prev) => ({ ...prev, [buildId]: c }));
    } catch (e) {
      console.error('Error loading pages:', e);
      setError('Could not load pages.');
    }
  }, []);

  useEffect(() => {
    setNotice(null);
    setError(null);
    if (!current) { setPages([]); return; }
    setPagesLoading(true);
    loadPages(current.id).finally(() => setPagesLoading(false));
  }, [current?.id, loadPages]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      console.error(e);
      setError('That did not save. Check the console for details.');
    }
    setBusy(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setConfirmDeleteBuild(false);
    setPanelOpen(true);
  };

  const openEdit = (b: ActiveBuild) => {
    setEditingId(b.id);
    setForm({
      client_id: b.client_id,
      name: b.name,
      notes: b.notes ?? '',
      status: b.status,
      assigned_to: b.assigned_to,
      freq: b.freq,
      interval_n: String(b.interval_n),
      byweekday: b.byweekday ?? [],
      bymonthday: String(b.bymonthday ?? Number(b.start_date.slice(8, 10))),
      start_date: b.start_date,
      hold_while_open: b.hold_while_open,
      auto_release: b.auto_release,
    });
    setFormError(null);
    setConfirmDeleteBuild(false);
    setPanelOpen(true);
  };

  const handleSave = async () => {
    if (!form.client_id) return setFormError('Pick a client');
    if (!form.name.trim()) return setFormError('Name the build');
    if (form.freq === 'weekly' && form.byweekday.length === 0) return setFormError('Pick at least one weekday');
    const payload: Partial<ActiveBuild> = {
      client_id: form.client_id,
      name: form.name.trim(),
      notes: form.notes.trim() || null,
      status: form.status,
      assigned_to: form.assigned_to,
      freq: form.freq,
      interval_n: Math.max(1, parseInt(form.interval_n, 10) || 1),
      byweekday: form.freq === 'weekly' ? form.byweekday : null,
      bymonthday: form.freq === 'monthly' ? Math.min(31, Math.max(1, parseInt(form.bymonthday, 10) || 1)) : null,
      start_date: form.start_date || formatForDateInput(getCurrentDate()),
      hold_while_open: form.hold_while_open,
      auto_release: form.auto_release,
    };
    setBusy(true);
    try {
      const id = await saveBuild(editingId, payload);
      setPanelOpen(false);
      await loadBuilds();
      setFilter(form.status);
      setBuildParam(id);
    } catch (e) {
      console.error('Error saving build:', e);
      setFormError('Failed to save. Check the console for details.');
    }
    setBusy(false);
  };

  const handleDeleteBuild = async () => {
    if (!editingId) return;
    await run(async () => {
      await deleteBuild(editingId);
      setPanelOpen(false);
      setBuildParam(null);
      await loadBuilds();
    });
  };

  const stats = useMemo(() => {
    const act = summaries.filter((s) => s.build.status === 'active');
    return {
      active: act.length,
      toBuild: act.reduce((n, s) => n + s.backlog, 0),
      onBoard: act.reduce((n, s) => n + s.queued, 0),
      auto: act.filter((s) => s.build.auto_release).length,
    };
  }, [summaries]);

  const backlogTop = pages.find((p) => p.status === 'backlog');

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader title="Active Builds" actions={<Button size="sm" onClick={openCreate}>New Build</Button>} />

        <div className="grid grid-cols-2 lg:grid-cols-4" style={{ gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
          <StatCard label="Active builds" value={stats.active} />
          <StatCard label="Pages to build" value={stats.toBuild} />
          <StatCard label="On the board" value={stats.onBoard} />
          <StatCard label="Auto-releasing" value={`${stats.auto} of ${stats.active}`} />
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <div className="animate-spin w-7 h-7 border-2 rounded-full" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <BuildsView
            summaries={summaries}
            filter={filter}
            onFilter={(s) => { setFilter(s); setBuildParam(null); }}
            selected={current && current.status === filter ? current : null}
            onSelect={(id) => setBuildParam(id)}
            pages={pages}
            pagesLoading={pagesLoading}
            assigneeName={users.find((u) => u.id === current?.assigned_to)?.name ?? null}
            nextRelease={current ? nextReleaseDay(current) : null}
            busy={busy}
            notice={notice}
            error={error}
            onEdit={() => current && openEdit(current)}
            onToggleAuto={(next) => current && run(async () => {
              await saveBuild(current.id, { auto_release: next, ...(next && current.status !== 'active' ? { status: 'active' as const } : {}) });
              await loadBuilds();
            })}
            onReleaseNext={() => current && run(async () => {
              const taskId = await releaseNext(current.id);
              setNotice(taskId && backlogTop ? `"${backlogTop.title}" is on the Fulfillment board for today.` : 'Backlog is empty.');
              await loadPages(current.id);
            })}
            onAddPage={(title, path, kind: PageKind) => current && run(async () => {
              await addPage(current.id, title, path, kind, pages.reduce((m, p) => Math.max(m, p.position), -1) + 1);
              await loadPages(current.id);
            })}
            onSetStatus={(p, status) => current && run(async () => {
              await updatePage(p.id, { status });
              await loadPages(current.id);
            })}
            onReorderBacklog={(ids) => {
              if (!current) return;
              const order = [...pages.filter((p) => p.status !== 'backlog').map((p) => p.id), ...ids];
              setPages((prev) => order.map((id, k) => ({ ...prev.find((x) => x.id === id)!, position: k })));
              run(() => reorderPages(order));
            }}
            onSavePage={(p, patch) => current && run(async () => {
              await updatePage(p.id, patch);
              await loadPages(current.id);
            })}
            onDeletePage={(p) => current && run(async () => {
              await deletePage(p.id);
              await loadPages(current.id);
            })}
          />
        )}
      </main>

      <SidePanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        eyebrow="Active Builds"
        title={editingId ? 'Build Settings' : 'New Build'}
        width={460}
        footer={
          <>
            <Button onClick={handleSave} disabled={busy}>{busy ? 'Saving…' : editingId ? 'Save changes' : 'Create'}</Button>
            <Button variant="ghost" onClick={() => setPanelOpen(false)} disabled={busy}>Cancel</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {formError ? (
            <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--status-down-soft)', color: 'var(--danger-400)', fontSize: 'var(--text-sm)' }}>
              {formError}
            </div>
          ) : null}

          <SearchableSelect
            label="Client"
            placeholder="Pick a client..."
            searchPlaceholder="Search clients..."
            options={clients.map((c) => ({ value: c.id, label: c.name }))}
            value={form.client_id}
            onChange={(v) => setForm((f) => ({ ...f, client_id: v }))}
          />
          <Input label="Build name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Website rebuild" />
          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Notes</div>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              className="ck-textarea"
              placeholder="Figma link, repo, preview URL…"
            />
          </div>
          <Dropdown
            label="Status"
            options={[
              { value: 'active', label: 'Active' },
              { value: 'paused', label: 'Paused' },
              { value: 'finished', label: 'Finished' },
            ]}
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as BuildStatus }))}
          />

          <div className="t-eyebrow" style={{ marginTop: '4px' }}>Release to the Fulfillment board</div>
          <Dropdown
            label="Assign tasks to"
            placeholder="Unassigned"
            options={[{ value: '', label: 'Unassigned' }, ...users.map((u) => ({ value: u.id, label: u.name }))]}
            value={form.assigned_to || ''}
            onChange={(e) => setForm((f) => ({ ...f, assigned_to: e.target.value || null }))}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <Dropdown
              label="Release one page"
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
              value={form.freq}
              onChange={(e) => setForm((f) => ({ ...f, freq: e.target.value as BuildFreq }))}
            />
            <Input
              label={form.freq === 'daily' ? 'Every N days' : form.freq === 'weekly' ? 'Every N weeks' : 'Every N months'}
              type="number"
              min={1}
              max={12}
              value={form.interval_n}
              onChange={(e) => setForm((f) => ({ ...f, interval_n: e.target.value }))}
            />
          </div>
          {form.freq === 'weekly' ? (
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '7px' }}>On days</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {DAY_LABELS.map((label, d) => {
                  const on = form.byweekday.includes(d);
                  return (
                    <button
                      key={label}
                      type="button"
                      className="pressable"
                      onClick={() => setForm((f) => ({
                        ...f,
                        byweekday: f.byweekday.includes(d) ? f.byweekday.filter((x) => x !== d) : [...f.byweekday, d],
                      }))}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 'var(--radius-sm)',
                        border: `1px solid ${on ? 'var(--accent)' : 'var(--border-hairline)'}`,
                        background: on ? 'var(--accent-soft-hover)' : 'var(--surface-raised)',
                        color: on ? 'var(--accent-text)' : 'var(--text-muted)',
                        fontSize: 'var(--text-xs)',
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          {form.freq === 'monthly' ? (
            <Input label="Day of month" type="number" min={1} max={31} value={form.bymonthday} onChange={(e) => setForm((f) => ({ ...f, bymonthday: e.target.value }))} />
          ) : null}
          <Input label="Starting" type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />

          {[
            { key: 'auto_release' as const, label: 'Auto-release', hint: 'At 6am on each release day, the top backlog page becomes a task on the Fulfillment board.' },
            { key: 'hold_while_open' as const, label: 'Wait for the open page', hint: 'Skip a release day while a page from this build is still on the board.' },
          ].map((opt) => (
            <div
              key={opt.key}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                padding: '10px 12px', background: 'var(--surface-raised)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)',
              }}
            >
              <div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{opt.label}</div>
                <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginTop: '2px' }}>{opt.hint}</div>
              </div>
              <Switch checked={form[opt.key]} onChange={(next) => setForm((f) => ({ ...f, [opt.key]: next }))} />
            </div>
          ))}

          {editingId ? (
            confirmDeleteBuild ? (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', flex: 1 }}>Delete this build and its page list? Tasks already on the board stay.</span>
                <Button size="sm" variant="danger" onClick={handleDeleteBuild} disabled={busy}>Delete</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteBuild(false)}>Keep</Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteBuild(true)}>Delete build</Button>
            )
          ) : null}
        </div>
      </SidePanel>
    </div>
  );
}

/** First Denver day after released_through that the cadence lands on (mirrors recurring_rule_matches). */
function nextReleaseDay(b: ActiveBuild): string | null {
  const today = new Date(`${formatForDateInput(getCurrentDate())}T12:00:00Z`);
  const start = new Date(`${b.start_date}T12:00:00Z`);
  const afterMark = new Date(new Date(`${b.released_through}T12:00:00Z`).getTime() + 86400000);
  let d = afterMark > today ? afterMark : today;
  for (let k = 0; k < 400; k++, d = new Date(d.getTime() + 86400000)) {
    if (d < start) continue;
    const dow = d.getUTCDay();
    const days = Math.round((d.getTime() - start.getTime()) / 86400000);
    if (b.freq === 'daily' && days % b.interval_n === 0) return d.toISOString().slice(0, 10);
    if (b.freq === 'weekly') {
      const set = b.byweekday?.length ? b.byweekday : [start.getUTCDay()];
      const weeks = Math.floor((days + start.getUTCDay() - dow) / 7);
      if (set.includes(dow) && weeks % b.interval_n === 0) return d.toISOString().slice(0, 10);
    }
    if (b.freq === 'monthly') {
      const months = (d.getUTCFullYear() - start.getUTCFullYear()) * 12 + d.getUTCMonth() - start.getUTCMonth();
      const target = b.bymonthday ?? start.getUTCDate();
      const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
      if (months % b.interval_n === 0 && (d.getUTCDate() === target || (d.getUTCDate() === last && target > last))) {
        return d.toISOString().slice(0, 10);
      }
    }
  }
  return null;
}
